import React, { useEffect, useRef, useState } from 'react';
import { useGame } from '../contexts/GameContext';
import { getSettings, getLayout, getAsset, getCustomSpriteKeys } from '../services/api';
import { Volume2, VolumeX } from 'lucide-react';
import { TEMPLATES } from '../game/mapGenerator';
import {
  GRID_COLS, GRID_ROWS, TILE_SIZE, CELL,
  PLAYER_SPAWN, HUB_EXIT, HUB, TUNNEL_ROW,
  BONUS, ENEMY_RELEASE, ENEMY_TYPES, ENEMY_HUB_POS,
  PHASE_TIMINGS, GHOST_MODE, SCATTER,
} from '../game/config';
import { aStar, getChaseTarget, getScatterTarget, findNearestWalkable } from '../game/enemyAI';
import { classifyAllTiles, MASK } from '../game/tileTypes';

// Extracted modules
import { C, GAME_W, GAME_H, HALF, IS_MOBILE, DIR, OPPOSITE, tx, ty } from '../game/gameConstants';
import { walkable, walkableWrap, wrapCoords } from '../game/gridUtils';
import { buildMapVisuals, repopulatePills } from '../game/mapBuilder';
import { updatePlayerMovement } from '../game/playerMovement';
import { updatePhases, updateFrightened, updateEnemies } from '../game/enemyController';

// Component
const GameView = () => {
  const { settings: ctxSettings, endGame } = useGame();
  const containerRef = useRef(null);
  const gameRef = useRef(null);
  const sfx = useRef({}); // { gameplay, dot, powerup, enemy_eat, gameover, attract }
  const scoreRef = useRef(0);
  const timeRef = useRef(90);
  const scoreDisplayRef = useRef(null);
  const timeDisplayRef = useRef(null);
  const livesDisplayRef = useRef(null);
  const stageDisplayRef = useRef(null);

  // Shared touch state — written by document-level touch handlers, read by Phaser game loop
  const touchStateRef = useRef({ touchDir: null, pendingDir: null, touchActive: false });

  // Timer pause flag — shared between React timer interval and Phaser game loop
  const timerPausedRef = useRef(false);
  const volumeRef = useRef({ music: 0.4, effects: 0.6 });
  const [ready, setReady] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [audioMuted, setAudioMuted] = useState(() => localStorage.getItem('palletman_muted') === '1');

  const toggleGameAudio = () => {
    setAudioMuted(prev => {
      const mute = !prev;
      localStorage.setItem('palletman_muted', mute ? '1' : '0');
      Object.values(sfx.current).forEach(a => {
        if (a) a.muted = mute;
      });
      return mute;
    });
  };

  // Load settings ONCE — guard against StrictMode double-execution
  const settingsLoadedRef = useRef(false);
  useEffect(() => {
    if (settingsLoadedRef.current) return;
    settingsLoadedRef.current = true;
    getSettings().then(s => {
      setSettings(s);
      timeRef.current = s?.game_duration || 90;
    }).catch(() => setSettings(ctxSettings || {}));
  }, [ctxSettings]);

  // Sync volume ref with settings and apply to loaded audio
  useEffect(() => {
    if (settings) {
      volumeRef.current.music = settings.volume_music ?? 0.4;
      volumeRef.current.effects = settings.volume_effects ?? 0.6;
      if (sfx.current.gameplay) sfx.current.gameplay.volume = volumeRef.current.music;
      if (sfx.current.powerup) sfx.current.powerup.volume = volumeRef.current.music;
      if (sfx.current.dot) sfx.current.dot.volume = volumeRef.current.effects;
      if (sfx.current.enemy_eat) sfx.current.enemy_eat.volume = volumeRef.current.effects;
      if (sfx.current.gameover) sfx.current.gameover.volume = volumeRef.current.effects;
    }
  }, [settings]);

  // Preload audio in a SEPARATE effect (runs once on mount)
  useEffect(() => {
    const audioIds = ['audio_gameplay', 'audio_dot', 'audio_powerup', 'audio_enemy_eat', 'audio_gameover'];
    let cancelled = false;

    async function loadAllAudio() {
      const results = await Promise.allSettled(
        audioIds.map(async (id) => {
          try {
            const asset = await getAsset(id);
            if (cancelled || !asset?.data || !asset?.mime_type) return;
            const audio = new Audio(`data:${asset.mime_type};base64,${asset.data}`);
            const key = id.replace('audio_', '');
            if (key === 'gameplay' || key === 'powerup') {
              audio.loop = true;
              audio.volume = volumeRef.current.music;
            } else {
              audio.volume = volumeRef.current.effects;
            }
            audio.preload = 'auto';
            audio.muted = localStorage.getItem('palletman_muted') === '1';
            audio.load();
            sfx.current[key] = audio;
            console.log(`Audio loaded: ${key}`);
          } catch (e) {
            console.warn(`Audio load failed: ${id}`, e);
          }
        })
      );
      if (!cancelled) {
        setAudioLoaded(true);
        console.log('All audio loaded, sfx keys:', Object.keys(sfx.current));
      }
    }

    loadAllAudio();
    return () => { cancelled = true; };
  }, []);

  // ── SWIPE LISTENERS on document (NEVER lost, bulletproof) ──
  useEffect(() => {
    const SWIPE_THRESHOLD = 6;
    let tSX = 0, tSY = 0;
    let tracking = false; // true if touch started inside game area
    const ts = touchStateRef.current;

    function isInGameArea(clientX, clientY) {
      const el = containerRef.current;
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
    }

    function detectDir(dx, dy) {
      const absDx = Math.abs(dx), absDy = Math.abs(dy);
      if (Math.max(absDx, absDy) < SWIPE_THRESHOLD) return null;
      return absDx > absDy ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
    }

    const onTouchStart = (e) => {
      try {
        const t = e.touches[0];
        if (!isInGameArea(t.clientX, t.clientY)) { tracking = false; return; }
        e.preventDefault();
        tSX = t.clientX; tSY = t.clientY;
        tracking = true;
        ts.touchActive = true;
      } catch (_) {}
    };
    const onTouchMove = (e) => {
      try {
        if (!tracking) return;
        e.preventDefault();
        const t = e.touches[0];
        const dir = detectDir(t.clientX - tSX, t.clientY - tSY);
        if (dir) {
          ts.touchDir = dir;
          tSX = t.clientX; tSY = t.clientY;
        }
      } catch (_) {}
    };
    const onTouchEnd = (e) => {
      try {
        if (!tracking) return;
        e.preventDefault();
        if (e.changedTouches[0]) {
          const t = e.changedTouches[0];
          const dir = detectDir(t.clientX - tSX, t.clientY - tSY);
          if (dir) ts.pendingDir = dir;
        }
        tracking = false;
        ts.touchActive = false;
        ts.touchDir = null;
      } catch (_) {}
    };
    const onTouchCancel = (e) => {
      try {
        if (!tracking) return;
        e.preventDefault();
        tracking = false;
        ts.touchActive = false;
        ts.touchDir = null;
      } catch (_) {}
    };

    document.addEventListener('touchstart', onTouchStart, { passive: false, capture: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    document.addEventListener('touchend', onTouchEnd, { passive: false, capture: true });
    document.addEventListener('touchcancel', onTouchCancel, { passive: false, capture: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart, { capture: true });
      document.removeEventListener('touchmove', onTouchMove, { capture: true });
      document.removeEventListener('touchend', onTouchEnd, { capture: true });
      document.removeEventListener('touchcancel', onTouchCancel, { capture: true });
    };
  }, []);

  // Timer — direct DOM update (no React re-renders during gameplay)
  useEffect(() => {
    if (!ready) return;
    const iv = setInterval(() => {
      // Pause timer during stage transitions and game over
      if (timerPausedRef.current) return;
      timeRef.current -= 1;
      if (timeDisplayRef.current) {
        timeDisplayRef.current.textContent = `${timeRef.current}s`;
      }
      if (timeRef.current <= 0) {
        clearInterval(iv);
        window.__palletmanEndGame?.('timeout');
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [ready]);

  // Phaser game
  useEffect(() => {
    if (!settings || gameRef.current) return;

    let currentDir = null;
    let nextDir = null;
    let hasUserInput = false;

    // Resize handler ref (used in initGame, cleaned up in return)
    const refreshScale = () => {
      if (gameRef.current?.scale) gameRef.current.scale.refresh();
    };

    window.setGameDirection = (dir) => { nextDir = dir; touchStateRef.current.pendingDir = dir; hasUserInput = true; };

    const gs = {
      grid: null,
      // Player tile-based state
      player: null,        // visual circle
      pCol: 0, pRow: 0,    // current grid tile
      pTx: 0, pTy: 0,      // target pixel position (center of target tile)
      pMoving: false,       // is player in motion between tiles
      // Enemy state
      enemies: [],
      // Collectibles stored in grid (no physics)
      pillSprites: {},      // key "col,row" -> sprite
      powerupSprites: {},
      bonusPallet: null,
      score: 0,
      gameOver: false,
      isFrightened: false,
      frightenedEnd: 0,
      startTime: 0,
      graceEnd: 0,
      basePlayerSpeed: settings?.player_speed || 200,
      bonusInterval: settings?.bonus_interval || BONUS.interval,
      bonusDuration: settings?.bonus_duration || BONUS.duration,
      bonusPoints: settings?.bonus_points || BONUS.points,
      lastBonusSpawn: 0,
      musicPhase: 'none', // 'none' | 'gameplay' | 'powerup' | 'gameover'
      // Chase/Scatter phase system
      ghostMode: GHOST_MODE.SCATTER,
      phaseIndex: 0,         // index into PHASE_TIMINGS
      phaseIsScatter: true,  // first phase = scatter
      phaseStartTime: 0,     // when the current phase began (game-time)
      modeJustChanged: false, // flag to reverse enemies on mode change
      // Progressive eat scoring (resets each frightened period)
      ghostEatCombo: 0,      // 0→200, 1→400, 2→800, 3→1600
      // Lives system
      lives: settings?.player_lives || 3,
      respawning: false,
      // Stage system
      stage: 1,
      maxStages: 3,
      stageTransitioning: false,
    };
    const GRACE_MS = 5000;

    // Direct DOM score update (avoids React re-renders during gameplay)
    function updateScoreUI() {
      scoreRef.current = gs.score;
      if (scoreDisplayRef.current) {
        scoreDisplayRef.current.textContent = `SCORE ${gs.score}`;
      }
    }

    // Direct DOM lives update
    function updateLivesUI() {
      if (livesDisplayRef.current) {
        const heroSrc = gs.customSprites?.hero
          ? process.env.REACT_APP_BACKEND_URL + '/api/game-sprites/hero/image'
          : '/assets/tiles/hero.png';
        livesDisplayRef.current.innerHTML = Array(Math.max(0, gs.lives)).fill(
          `<img src="${heroSrc}" style="width:clamp(24px,3.5vh,40px);height:clamp(24px,3.5vh,40px);object-fit:contain" alt="" />`
        ).join('');
      }
    }

    // Handle losing a life (called on enemy collision)
    function loseLife(scene, time) {
      gs.lives--;
      updateLivesUI();

      if (gs.lives <= 0) {
        window.__palletmanEndGame('gameover');
        return;
      }

      gs.respawning = true;
      gs.enemies.forEach(e => e.setVisible(false));

      // Death animation: spin + shrink + flash red
      scene.tweens.add({
        targets: gs.player,
        angle: 720,
        scaleX: 0,
        scaleY: 0,
        alpha: 0,
        duration: 900,
        ease: 'Power3',
        onStart: () => { gs.player.setTint(0xff0000); },
        onComplete: () => {
          gs.player.setVisible(false);
          gs.player.setAngle(0);
          gs.player.setScale(1);
          gs.player.setAlpha(1);
          gs.player.clearTint();
        },
      });

      // End frightened state if active
      if (gs.isFrightened) {
        gs.isFrightened = false;
        gs.ghostEatCombo = 0;
        gs.enemies.forEach(e => { e.isScared = false; });
        if (sfx.current.powerup) sfx.current.powerup.pause();
      }

      // Pause music
      if (sfx.current.gameplay) sfx.current.gameplay.pause();

      // "- 1" death text
      const deathText = scene.add.text(GAME_W / 2, GAME_H / 2, '- 1', {
        fontFamily: 'monospace', fontSize: '64px', color: '#FF4444',
        fontStyle: 'bold', stroke: '#000', strokeThickness: 8,
        shadow: { offsetX: 0, offsetY: 0, color: '#FF0000', blur: 20, fill: true },
      }).setOrigin(0.5).setDepth(150);
      scene.tweens.add({
        targets: deathText, alpha: 0, y: deathText.y - 60, scale: 1.5,
        duration: 1200, ease: 'Power2',
        onComplete: () => deathText.destroy(),
      });

      // Respawn after 1.5s
      scene.time.delayedCall(1500, () => {
        if (gs.gameOver) return;

        // Respawn player at start
        gs.pCol = PLAYER_SPAWN.col;
        gs.pRow = PLAYER_SPAWN.row;
        gs.pTx = tx(gs.pCol);
        gs.pTy = ty(gs.pRow);
        gs.pMoving = false;
        gs.player.x = gs.pTx;
        gs.player.y = gs.pTy;
        gs.player.setVisible(true);
        gs.player.setTexture('hero');
        gs.player.setFlipX(false);
        gs.player.setFlipY(false);

        // Reset enemies to hub
        gs.enemies.forEach((enemy, idx) => {
          const pos = ENEMY_HUB_POS[idx];
          enemy.col = pos.col;
          enemy.row = pos.row;
          enemy.x = tx(pos.col);
          enemy.y = ty(pos.row);
          enemy.targetX = tx(pos.col);
          enemy.targetY = ty(pos.row);
          enemy.moving = false;
          enemy.isInHub = true;
          enemy.released = true;
          enemy.isScared = false;
          enemy.eaten = false;
          enemy.path = [];
          enemy.pathIdx = 0;
          enemy.hubGrace = 0;
          enemy.setVisible(true);
          enemy.setTexture(enemy.ghostKey);
          enemy.clearTint();
          enemy.setAlpha(1);
        });

        // Reset direction
        currentDir = null;
        nextDir = null;
        touchStateRef.current.touchDir = null;
        touchStateRef.current.pendingDir = null;

        // New grace period
        gs.graceEnd = scene.time.now + GRACE_MS;
        gs.respawning = false;

        // Resume gameplay music
        gs.musicPhase = 'gameplay';
        if (sfx.current.gameplay) {
          sfx.current.gameplay.volume = volumeRef.current.music;
          sfx.current.gameplay.play().catch(() => {});
        }
      });
    }

    // Update stage display in HUD
    function updateStageUI() {
      if (stageDisplayRef.current) {
        stageDisplayRef.current.textContent = `STAGE ${gs.stage}`;
      }
    }

    // Transition to next stage
    function nextStage(scene) {
      gs.stageTransitioning = true;

      // Pause music
      if (sfx.current.gameplay) sfx.current.gameplay.pause();
      if (sfx.current.powerup) sfx.current.powerup.pause();

      // End frightened if active
      if (gs.isFrightened) {
        gs.isFrightened = false;
        gs.ghostEatCombo = 0;
        gs.enemies.forEach(e => { e.isScared = false; e.setTexture(e.ghostKey); e.clearTint(); });
      }

      // Hide player and enemies
      gs.player.setVisible(false);
      gs.enemies.forEach(e => e.setVisible(false));

      // Remove bonus pallet if present
      if (gs.bonusPallet) { gs.bonusPallet.destroy(); gs.bonusPallet = null; }

      // "STAGE COMPLETE" text
      const completeText = scene.add.text(GAME_W / 2, GAME_H / 2 - 40, `STAGE ${gs.stage}\nCOMPLETE!`, {
        fontFamily: 'monospace', fontSize: '56px', color: '#00FF88',
        fontStyle: 'bold', stroke: '#000', strokeThickness: 8,
        align: 'center', lineSpacing: 4,
        shadow: { offsetX: 0, offsetY: 0, color: '#00FF88', blur: 30, fill: true },
      }).setOrigin(0.5).setDepth(150).setAlpha(0).setScale(0.3);
      scene.tweens.add({ targets: completeText, alpha: 1, scale: 1, duration: 500, ease: 'Back.easeOut' });

      // After 2.5s: setup next stage
      scene.time.delayedCall(2500, () => {
        if (gs.gameOver) return;
        completeText.destroy();

        // Advance stage (lives carry over — no reset)
        gs.stage++;
        updateStageUI();
        updateLivesUI();

        // Reset timer to admin-configured duration
        timeRef.current = settings?.game_duration || 90;
        if (timeDisplayRef.current) timeDisplayRef.current.textContent = `${timeRef.current}s`;

        // Rebuild entire map visuals for new stage layout
        buildMapVisuals(scene, gs, settings);

        // Reset player
        gs.pCol = PLAYER_SPAWN.col;
        gs.pRow = PLAYER_SPAWN.row;
        gs.pTx = tx(gs.pCol);
        gs.pTy = ty(gs.pRow);
        gs.pMoving = false;
        gs.player.x = gs.pTx;
        gs.player.y = gs.pTy;
        gs.player.setVisible(true);
        gs.player.setTexture('hero');
        gs.player.setFlipX(false);
        gs.player.setFlipY(false);

        // Reset enemies to hub
        gs.enemies.forEach((enemy, idx) => {
          const pos = ENEMY_HUB_POS[idx];
          enemy.col = pos.col;
          enemy.row = pos.row;
          enemy.x = tx(pos.col);
          enemy.y = ty(pos.row);
          enemy.targetX = tx(pos.col);
          enemy.targetY = ty(pos.row);
          enemy.moving = false;
          enemy.isInHub = true;
          enemy.released = true;
          enemy.isScared = false;
          enemy.eaten = false;
          enemy.path = [];
          enemy.pathIdx = 0;
          enemy.hubGrace = 0;
          enemy.setVisible(true);
          enemy.setTexture(enemy.ghostKey);
          enemy.clearTint();
          enemy.setAlpha(1);
        });

        // Reset direction
        currentDir = 'right';
        nextDir = 'right';

        // "STAGE X - READY!" text
        const readyText = scene.add.text(GAME_W / 2, GAME_H / 2, `STAGE ${gs.stage}`, {
          fontFamily: 'monospace', fontSize: '56px', color: '#FFD100',
          fontStyle: 'bold', stroke: '#000', strokeThickness: 8,
          shadow: { offsetX: 0, offsetY: 0, color: '#FFD100', blur: 30, fill: true },
        }).setOrigin(0.5).setDepth(150);
        scene.tweens.add({ targets: readyText, alpha: { from: 1, to: 0.2 }, duration: 400, yoyo: true, repeat: -1 });

        // Grace period
        gs.graceEnd = scene.time.now + GRACE_MS;

        scene.time.delayedCall(GRACE_MS, () => {
          readyText.destroy();
          gs.stageTransitioning = false;
          timerPausedRef.current = false; // Resume timer for new stage
          // Resume music
          gs.musicPhase = 'gameplay';
          if (sfx.current.gameplay) {
            sfx.current.gameplay.currentTime = 0;
            sfx.current.gameplay.volume = volumeRef.current.music;
            sfx.current.gameplay.play().catch(() => {});
          }
        });
      });
    }

    // Check if all pills are collected (stage complete)
    function checkStageComplete(scene) {
      if (Object.keys(gs.pillSprites).length === 0 && Object.keys(gs.powerupSprites).length === 0) {
        timerPausedRef.current = true; // Stop timer immediately
        if (gs.stage >= gs.maxStages) {
          window.__palletmanEndGame('victory');
        } else {
          nextStage(scene);
        }
      }
    }

    window.__palletmanEndGame = (type) => {
      if (gs.gameOver) return;
      gs.gameOver = true;
      timerPausedRef.current = true; // Stop timer
      
      // Capture final score immediately from gs (single source of truth)
      const capturedScore = gs.score;
      scoreRef.current = capturedScore;
      updateScoreUI();
      
      if (gameRef.current?.scene?.scenes?.[0]) {
        const scene = gameRef.current.scene.scenes[0];
        scene.physics?.pause?.();
        
        // Hide player and enemies
        gs.player?.setVisible(false);
        gs.enemies?.forEach(e => e?.setVisible(false));
        
        // Semi-transparent overlay — dark enough to read text, game peeks through
        const overlay = scene.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0x000000, 0)
          .setDepth(200).setOrigin(0.5);
        scene.tweens.add({ targets: overlay, fillAlpha: 0.8, duration: 500, ease: 'Power2' });
        
        // Subtle scanlines for retro CRT feel (single texture instead of 432 rectangles)
        if (!IS_MOBILE) {
          const slGfx = scene.add.graphics().setDepth(201);
          slGfx.fillStyle(0x000000, 0.12);
          for (let y = 0; y < GAME_H; y += 4) {
            slGfx.fillRect(0, y, GAME_W, 1);
          }
        }
        
        // GAME OVER / TEMPO SCADUTO / VICTORY — massive retro text
        let goText, goColor;
        if (type === 'victory') {
          goText = 'HAI\nVINTO!';
          goColor = '#FFD100';
        } else if (type === 'timeout') {
          goText = 'TEMPO\nSCADUTO!';
          goColor = '#FFD100';
        } else {
          goText = 'GAME\nOVER';
          goColor = '#FF0000';
        }
        const mainTxt = scene.add.text(GAME_W / 2, GAME_H / 2 - 120, goText, {
          fontFamily: 'monospace',
          fontSize: '130px',
          color: goColor,
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 16,
          align: 'center',
          lineSpacing: 4,
          shadow: { offsetX: 0, offsetY: 0, color: goColor, blur: 40, fill: true },
        }).setOrigin(0.5).setDepth(202).setAlpha(0).setScale(3);
        
        scene.tweens.add({
          targets: mainTxt,
          alpha: 1, scale: 1,
          duration: 600, delay: 150,
          ease: 'Back.easeOut',
        });
        
        // Blink after zoom-in
        scene.time.delayedCall(900, () => {
          scene.tweens.add({
            targets: mainTxt, alpha: 0.3,
            duration: 400, yoyo: true, repeat: -1,
            ease: 'Sine.easeInOut',
          });
        });

        // Confetti for victory
        if (type === 'victory') {
          const confettiColors = [0xFFD100, 0xFF4444, 0x44FF44, 0x4488FF, 0xFF88FF, 0xFF8800];
          for (let i = 0; i < 40; i++) {
            const gfx = scene.add.graphics().setDepth(203);
            gfx.fillStyle(confettiColors[i % confettiColors.length], 1);
            gfx.fillRect(0, 0, Phaser.Math.Between(4, 10), Phaser.Math.Between(4, 10));
            gfx.x = Phaser.Math.Between(20, GAME_W - 20);
            gfx.y = GAME_H + 20;
            scene.tweens.add({
              targets: gfx,
              y: Phaser.Math.Between(-50, GAME_H * 0.4),
              x: gfx.x + Phaser.Math.Between(-120, 120),
              angle: Phaser.Math.Between(0, 720),
              duration: Phaser.Math.Between(1200, 2800),
              delay: Phaser.Math.Between(0, 1200),
              ease: 'Power2',
              onComplete: () => {
                scene.tweens.add({ targets: gfx, alpha: 0, y: gfx.y + 60, duration: 600, onComplete: () => gfx.destroy() });
              },
            });
          }
        }
        
        // Score
        const finalScoreVal = capturedScore;
        const scoreTxt = scene.add.text(GAME_W / 2, GAME_H / 2 + 140, `SCORE: ${finalScoreVal.toLocaleString()}`, {
          fontFamily: 'monospace',
          fontSize: '36px',
          color: '#FFD100',
          fontStyle: 'bold',
          stroke: '#000',
          strokeThickness: 8,
          shadow: { offsetX: 0, offsetY: 0, color: '#FFD100', blur: 20, fill: true },
        }).setOrigin(0.5).setDepth(202).setAlpha(0);
        scene.tweens.add({ targets: scoreTxt, alpha: 1, duration: 400, delay: 650 });
        
        // Score tier title
        const t1 = settings?.tier1_threshold || 5000;
        const t2 = settings?.tier2_threshold || 15000;
        let tierTitle;
        if (finalScoreVal <= t1) {
          tierTitle = settings?.tier1_title_it || 'Apprendista Logistico';
        } else if (finalScoreVal <= t2) {
          tierTitle = settings?.tier2_title_it || 'Export Manager';
        } else {
          tierTitle = settings?.tier3_title_it || 'Export Manager Livello PRO!';
        }
        
        const tierTxt = scene.add.text(GAME_W / 2, GAME_H / 2 + 210, tierTitle, {
          fontFamily: 'monospace',
          fontSize: '24px',
          color: '#00BFFF',
          fontStyle: 'bold',
          stroke: '#000',
          strokeThickness: 5,
          shadow: { offsetX: 0, offsetY: 0, color: '#00BFFF', blur: 15, fill: true },
        }).setOrigin(0.5).setDepth(202).setAlpha(0);
        
        scene.tweens.add({
          targets: tierTxt, alpha: 1, duration: 400, delay: 950,
          onComplete: () => {
            scene.tweens.add({ targets: tierTxt, alpha: 0.4, duration: 500, yoyo: true, repeat: -1 });
          }
        });
      }
      
      // Stop music, play gameover sound (detached from sfx so cleanup won't pause it)
      gs.musicPhase = 'gameover';
      sfx.current.gameplay?.pause();
      sfx.current.powerup?.pause();
      if (sfx.current.gameover) {
        const goAudio = sfx.current.gameover;
        delete sfx.current.gameover;
        goAudio.currentTime = 0;
        goAudio.volume = volumeRef.current.effects;
        goAudio.play().catch(() => {});
      }
      const finalScore = capturedScore;
      const finalType = type || 'gameover';
      setTimeout(() => {
        try { gameRef.current?.destroy(true); } catch (e) { console.warn('Phaser destroy error:', e); }
        gameRef.current = null;
        endGame(finalScore, finalType);
      }, 3000);
    };

    // ── Load layouts + custom sprites, then start Phaser ──
    function preloadAll() {
      Promise.all([
        getLayout(0), getLayout(1), getLayout(2),
        getCustomSpriteKeys().catch(() => ({})),
      ])
        .then(([l0, l1, l2, customKeys]) => {
          gs.savedLayouts = { 0: l0, 1: l1, 2: l2 };
          gs.customSprites = customKeys || {};
        })
        .catch(() => { gs.savedLayouts = {}; gs.customSprites = {}; })
        .finally(() => {
          setLoading(false);
          if (window.Phaser) return initGame();
          const s = document.createElement('script');
          s.src = 'https://cdn.jsdelivr.net/npm/phaser@3.60.0/dist/phaser.min.js';
          s.onload = initGame;
          document.head.appendChild(s);
        });
    }

    function initGame() {
      if (gameRef.current) return;
      gameRef.current = new window.Phaser.Game({
        type: window.Phaser.AUTO,
        parent: containerRef.current,
        width: GAME_W,
        height: GAME_H,
        backgroundColor: C.bg,
        pixelArt: true,
        roundPixels: true,
        input: { touch: false, activePointers: 1 },
        render: { pixelArt: true, roundPixels: true },
        scale: { mode: window.Phaser.Scale.FIT, autoCenter: window.Phaser.Scale.CENTER_BOTH },
        scene: {
          preload() { preloadAssets(this); },
          create() { createScene(this); },
          update(time, delta) { updateScene(this, time, delta); },
        },
      });
      // Ensure touch-action: none on the actual canvas element
      const canvasEl = containerRef.current?.querySelector('canvas');
      if (canvasEl) {
        canvasEl.style.touchAction = 'none';
      }

      // Force Phaser to recalculate scale on viewport changes (address bar, rotation)
      window.addEventListener('resize', refreshScale);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', refreshScale);
      }
    }

    // ── PRELOAD TILE ASSETS ──
    function preloadAssets(scene) {
      const base = '/assets/tiles/';
      const apiBase = process.env.REACT_APP_BACKEND_URL + '/api/game-sprites/';
      const custom = gs.customSprites || {};

      // Helper: load from API if custom override exists, otherwise from default file
      function spr(phaserKey, spriteKey, defaultFile) {
        scene.load.image(phaserKey, custom[spriteKey] ? apiBase + spriteKey + '/image' : base + defaultFile);
      }

      // Road tiles
      spr('tile_floor', 'tile_floor', 'tile_floor.png');
      spr('mark_cross', 'tile_marking_cross', 'tile_marking_cross.png');
      spr('mark_h', 'tile_marking_horizontal', 'tile_marking_horizontal.png');
      spr('mark_v', 'tile_marking_vertical', 'tile_marking_vertical.png');
      // Wall tiles
      spr('wall_fill', 'wall_fill', 'wall_fill.png');
      spr('wall_edge_top', 'wall_edge_top', 'wall_edge_top.png');
      spr('wall_edge_bottom', 'wall_edge_bottom', 'wall_edge_bottom.png');
      spr('wall_edge_left', 'wall_edge_left', 'wall_edge_left.png');
      spr('wall_edge_right', 'wall_edge_right', 'wall_edge_right.png');
      spr('wall_corner_tl', 'wall_corner_tl', 'wall_corner_tl.png');
      spr('wall_corner_tr', 'wall_corner_tr', 'wall_corner_tr.png');
      spr('wall_corner_bl', 'wall_corner_bl', 'wall_corner_bl.png');
      spr('wall_corner_br', 'wall_corner_br', 'wall_corner_br.png');
      spr('wall_edge_horizontal', 'wall_edge_horizontal', 'wall_edge_horizontal.png');
      spr('wall_edge_vertical', 'wall_edge_vertical', 'wall_edge_vertical.png');
      spr('tile1_top', 'tile1_top', 'tile1_top.png');
      spr('tile1_bottom', 'tile1_bottom', 'tile1_bottom.png');
      spr('tile1_left', 'tile1_left', 'tile1_left.png');
      spr('tile1_right', 'tile1_right', 'tile1_right.png');
      spr('tile1x1', 'tile1x1', 'tile1x1.png');
      // Characters
      spr('hero', 'hero', 'hero.png');
      spr('hero_vert', 'hero_vertical', 'hero_vertical.png');
      spr('ghost_red', 'red_ghost', 'red_ghost.png');
      spr('ghost_pink', 'pink_ghost', 'pink_ghost.png');
      spr('ghost_blue', 'blue_ghost', 'blue_ghost.png');
      spr('ghost_yellow', 'yellow_ghost', 'yellow_ghost.png');
      // Game elements
      spr('hub_sprite', 'hub', 'hub.png');
      spr('coin', 'coin', 'coin.png');
      spr('powerup_sprite', 'powerup', 'powerup.png');
      spr('bonus_fruit', 'bonus_fruit', 'bonus_fruit.png');
      // Decorations
      spr('decor_3x2', 'decor_warehouse_3x2_01', 'decor_warehouse_3x2_01.png');
      spr('decor_3x1', 'decor_warehouse_3x1_01', 'decor_warehouse_3x1_01.png');
      spr('decor_2x2', 'decor_warehouse_2x2_01', 'decor_warehouse_2x2_01.png');
      spr('decor_2x1', 'decor_warehouse_2x1_01', 'decor_warehouse_2x1_01.png');
      spr('decor_1x1', 'decor_warehouse_1x1_01', 'decor_warehouse_1x1_01.png');
      spr('decor_4x1', 'decor_warehouse_4x1_01', 'decor_warehouse_4x1_01.png');
      spr('decor_platform', 'decor_platform_4x1_01', 'decor_platform_4x1_01.png');
      spr('decor_hub_lg', 'decor_hub_center', 'decor_hub_center.png');
      // Props
      spr('prop_01', 'prop_icon_01', 'prop_icon_01.png');
      spr('prop_02', 'prop_icon_02', 'prop_icon_02.png');
      spr('prop_03', 'prop_icon_03', 'prop_icon_03.png');
      spr('prop_04', 'prop_icon_04', 'prop_icon_04.png');
      spr('prop_2x1', 'prop_icon_2x1_1', 'prop_icon_2x1_1.png');

      // Preload custom editor assets used in saved layouts
      const allPlacements = Object.values(gs.savedLayouts || {}).flatMap(l => l?.placements || []);
      const customKeys = new Set(allPlacements.map(p => p.asset_key).filter(k => k.startsWith('custom_')));
      customKeys.forEach(k => {
        scene.load.image(k, base + k + '.png');
      });
    }

    // ── CREATE ──
    function createScene(scene) {
      // Build map visuals and pills for Stage 1
      buildMapVisuals(scene, gs, settings);

      // Player
      gs.pCol = PLAYER_SPAWN.col;
      gs.pRow = PLAYER_SPAWN.row;
      gs.pTx = tx(gs.pCol);
      gs.pTy = ty(gs.pRow);
      gs.pMoving = false;
      gs.player = scene.add.image(gs.pTx, gs.pTy, 'hero').setDepth(10);

      // Enemies (tile-based, no physics)
      gs.enemies = [];
      ENEMY_TYPES.forEach((type, idx) => {
        const pos = ENEMY_HUB_POS[idx];
        const ghostKey = { blinky: 'ghost_red', pinky: 'ghost_pink', inky: 'ghost_blue', clyde: 'ghost_yellow' }[type];
        const enemy = scene.add.image(tx(pos.col), ty(pos.row), ghostKey).setDepth(10);
        enemy.enemyType = type;
        enemy.ghostKey = ghostKey;
        enemy.col = pos.col;
        enemy.row = pos.row;
        enemy.targetX = tx(pos.col);
        enemy.targetY = ty(pos.row);
        enemy.moving = false;
        enemy.dir = null;
        enemy.released = false;
        enemy.isInHub = true;
        enemy.isScared = false;
        enemy.path = [];
        enemy.pathIdx = 0;
        enemy.lastPathCalc = 0;
        enemy.eaten = false;
        enemy.respawnTime = 0;
        enemy.hubGrace = 0;
        gs.enemies.push(enemy);
      });

      gs.startTime = scene.time.now;
      gs.graceEnd = scene.time.now + GRACE_MS;
      gs.phaseStartTime = scene.time.now + GRACE_MS; // phases start after grace period

      // READY text
      const readyText = scene.add.text(GAME_W / 2, GAME_H / 2, 'READY!', {
        fontFamily: 'monospace', fontSize: '48px', color: '#ffdd00',
        fontStyle: 'bold', stroke: '#000', strokeThickness: 6,
      }).setOrigin(0.5).setDepth(100);
      scene.tweens.add({ targets: readyText, alpha: { from: 1, to: 0.2 }, duration: 400, yoyo: true, repeat: -1 });
      scene.time.delayedCall(GRACE_MS, () => {
        readyText.destroy();
        // Start music after READY
        gs.musicPhase = 'gameplay';
        if (sfx.current.gameplay) {
          sfx.current.gameplay.currentTime = 0;
          sfx.current.gameplay.volume = volumeRef.current.music;
          sfx.current.gameplay.play().catch(() => {});
        }
      });

      // Start auto-move right
      currentDir = 'right';
      nextDir = 'right';

      // Setup Phaser keyboard input (reliable, no DOM conflicts)
      scene.cursors = scene.input.keyboard.createCursorKeys();
      scene.wasd = scene.input.keyboard.addKeys({
        up: window.Phaser.Input.Keyboard.KeyCodes.W,
        down: window.Phaser.Input.Keyboard.KeyCodes.S,
        left: window.Phaser.Input.Keyboard.KeyCodes.A,
        right: window.Phaser.Input.Keyboard.KeyCodes.D,
      });

      // Touch is handled by React useEffect on container (not Phaser)
      // Ensure touch-action: none on canvas (critical for Android)
      if (scene.sys.game.canvas) {
        scene.sys.game.canvas.style.touchAction = 'none';
      }

      setReady(true);
      updateLivesUI();
      updateStageUI();
    }

    // ── UPDATE ──
    function updateScene(scene, time, delta) {
      if (gs.gameOver || !gs.player || gs.respawning || gs.stageTransitioning) return;
      const grid = gs.grid;
      // Stage-based player speed: +25% per stage (L1=1.0, L2=1.25, L3=1.50)
      const STAGE_PLAYER_MUL = [1.0, 1.25, 1.50];
      const playerStageMul = STAGE_PLAYER_MUL[gs.stage - 1] || 1.50;
      const spd = gs.basePlayerSpeed * playerStageMul * (delta / 1000);

      // Retry music only if we're in gameplay phase and audio is paused (autoplay may have blocked initial play)
      if (gs.musicPhase === 'gameplay' && sfx.current.gameplay && sfx.current.gameplay.paused) {
        sfx.current.gameplay.volume = volumeRef.current.music;
        sfx.current.gameplay.play().catch(() => {});
      }

      // Read Phaser keyboard input each frame
      if (scene.cursors) {
        if (scene.cursors.up.isDown || scene.wasd.up.isDown) { nextDir = 'up'; hasUserInput = true; }
        else if (scene.cursors.down.isDown || scene.wasd.down.isDown) { nextDir = 'down'; hasUserInput = true; }
        else if (scene.cursors.left.isDown || scene.wasd.left.isDown) { nextDir = 'left'; hasUserInput = true; }
        else if (scene.cursors.right.isDown || scene.wasd.right.isDown) { nextDir = 'right'; hasUserInput = true; }
      }

      // One-shot touch direction (from quick swipe / touchend)
      const ts = touchStateRef.current;
      if (ts.pendingDir) {
        nextDir = ts.pendingDir;
        hasUserInput = true;
        ts.pendingDir = null;
      }
      // Continuous swipe direction (finger on screen = like key held)
      if (ts.touchActive && ts.touchDir) {
        nextDir = ts.touchDir;
        hasUserInput = true;
      }

      // Grace blink
      if (time < gs.graceEnd) {
        gs.player.setAlpha(Math.floor(time / 150) % 2 === 0 ? 1 : 0.4);
      } else if (gs.player.alpha !== 1) {
        gs.player.setAlpha(1);
      }

      // ── PLAYER MOVEMENT (tile-based, responsive) ──
      const PRE_TURN_DIST = TILE_SIZE * 0.35; // how close to center to allow early turns

      // Collect items at current tile during movement (not just at center)
      const curCol = Math.round((gs.player.x - HALF) / TILE_SIZE);
      const curRow = Math.round((gs.player.y - HALF) / TILE_SIZE);
      if (curCol >= 0 && curCol < GRID_COLS && curRow >= 0 && curRow < GRID_ROWS) {
        collectAt(curCol, curRow, scene);
      }

      if (gs.pMoving) {
        const dx = gs.pTx - gs.player.x;
        const dy = gs.pTy - gs.player.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Immediate reversal: if nextDir is opposite of currentDir, reverse now
        if (nextDir && OPPOSITE[nextDir] === currentDir) {
          const prevCol = gs.pCol - DIR[currentDir][0];
          const prevRow = gs.pRow - DIR[currentDir][1];
          if (walkableWrap(grid, prevCol, prevRow)) {
            const w = wrapCoords(prevCol, prevRow);
            gs.pCol = w.col;
            gs.pRow = w.row;
            gs.pTx = tx(gs.pCol);
            gs.pTy = ty(gs.pRow);
            currentDir = nextDir;
            if (prevCol < 0 || prevCol >= GRID_COLS) { gs.player.x = gs.pTx; }
            if (prevRow < 0 || prevRow >= GRID_ROWS) { gs.player.y = gs.pTy; }
          }
        }
        // Pre-turn: close to target center, try perpendicular turn early
        else if (nextDir && nextDir !== currentDir && dist <= PRE_TURN_DIST) {
          const nd = DIR[nextDir];
          const snapCol = Math.round((gs.player.x - HALF) / TILE_SIZE);
          const snapRow = Math.round((gs.player.y - HALF) / TILE_SIZE);
          const nc = snapCol + nd[0], nr = snapRow + nd[1];
          if (nd && walkableWrap(grid, nc, nr)) {
            gs.player.x = tx(snapCol);
            gs.player.y = ty(snapRow);
            const w = wrapCoords(nc, nr);
            gs.pCol = w.col;
            gs.pRow = w.row;
            gs.pTx = tx(gs.pCol);
            gs.pTy = ty(gs.pRow);
            currentDir = nextDir;
            gs.pMoving = true;
            if (nc < 0 || nc >= GRID_COLS) { gs.player.x = gs.pTx; }
            if (nr < 0 || nr >= GRID_ROWS) { gs.player.y = gs.pTy; }
            collectAt(snapCol, snapRow, scene);
          } else if (dist <= spd + 1) {
            gs.player.x = gs.pTx;
            gs.player.y = gs.pTy;
            gs.pMoving = false;
            collectAt(gs.pCol, gs.pRow, scene);
          } else {
            gs.player.x += (dx / dist) * spd;
            gs.player.y += (dy / dist) * spd;
          }
        }
        else if (dist <= spd + 1) {
          gs.player.x = gs.pTx;
          gs.player.y = gs.pTy;
          gs.pMoving = false;
          collectAt(gs.pCol, gs.pRow, scene);
        } else {
          gs.player.x += (dx / dist) * spd;
          gs.player.y += (dy / dist) * spd;
        }
      }

      if (!gs.pMoving) {
        // At tile center — decide next move
        if (nextDir) {
          const nd = DIR[nextDir];
          const nc = gs.pCol + nd[0], nr = gs.pRow + nd[1];
          if (nd && walkableWrap(grid, nc, nr)) {
            currentDir = nextDir;
          }
          // Always consume: touch re-applies every frame while finger is down,
          // keyboard re-applies every frame while key is held.
          // After lift/release, pac-man continues in currentDir.
          nextDir = null;
        }
        if (currentDir) {
          const cd = DIR[currentDir];
          const nc = gs.pCol + cd[0], nr = gs.pRow + cd[1];
          if (cd && walkableWrap(grid, nc, nr)) {
            // Wrap coordinates if out of bounds
            const w = wrapCoords(nc, nr);
            gs.pCol = w.col;
            gs.pRow = w.row;
            gs.pTx = tx(gs.pCol);
            gs.pTy = ty(gs.pRow);
            gs.pMoving = true;
            // Teleport player to correct side if wrapping
            if (nc < 0 || nc >= GRID_COLS) { gs.player.x = gs.pTx; }
            if (nr < 0 || nr >= GRID_ROWS) { gs.player.y = gs.pTy; }
          }
          // Wall hit → stop and wait for user input
        }
      }

      // Truck direction flip + texture swap
      if (currentDir === 'left') {
        gs.player.setTexture('hero');
        gs.player.setFlipX(true);
        gs.player.setFlipY(false);
      } else if (currentDir === 'right') {
        gs.player.setTexture('hero');
        gs.player.setFlipX(false);
        gs.player.setFlipY(false);
      } else if (currentDir === 'up') {
        gs.player.setTexture('hero_vert');
        gs.player.setFlipX(false);
        gs.player.setFlipY(false);
      } else if (currentDir === 'down') {
        gs.player.setTexture('hero_vert');
        gs.player.setFlipX(false);
        gs.player.setFlipY(true);
      }

      // ── ENEMY LOGIC ──
      const elapsed = (time - gs.startTime) / 1000;

      // Stage-based enemy speed: L1 slower, L2 slightly faster, L3 equal to player
      const STAGE_ENEMY_RATIO = [0.70, 0.85, 1.0];
      const enemyRatio = STAGE_ENEMY_RATIO[gs.stage - 1] || 1.0;
      const baseEnemySpd = gs.basePlayerSpeed * playerStageMul * enemyRatio * (delta / 1000);

      // ── Chase/Scatter phase management ──
      if (!gs.isFrightened) {
        const phaseElapsed = (time - gs.phaseStartTime) / 1000;
        const pt = PHASE_TIMINGS[Math.min(gs.phaseIndex, PHASE_TIMINGS.length - 1)];
        const phaseDuration = gs.phaseIsScatter ? pt.scatter : pt.chase;

        if (phaseElapsed >= phaseDuration && phaseDuration !== Infinity) {
          // Switch phase
          if (gs.phaseIsScatter) {
            gs.phaseIsScatter = false; // scatter → chase
          } else {
            gs.phaseIsScatter = true;  // chase → scatter
            gs.phaseIndex = Math.min(gs.phaseIndex + 1, PHASE_TIMINGS.length - 1);
          }
          gs.ghostMode = gs.phaseIsScatter ? GHOST_MODE.SCATTER : GHOST_MODE.CHASE;
          gs.phaseStartTime = time;
          gs.modeJustChanged = true; // signal enemies to reverse
        }
      }

      // End frightened
      if (gs.isFrightened && time >= gs.frightenedEnd) {
        gs.isFrightened = false;
        gs.ghostEatCombo = 0;
        gs.enemies.forEach(e => {
          e.isScared = false;
          e.setTexture(e.ghostKey);
          e.clearTint();
          // Brief grace after frightened ends to avoid unfair kills
          e.hubGrace = time + 500;
        });
        // Switch music: pause powerup, resume gameplay
        gs.musicPhase = 'gameplay';
        if (sfx.current.powerup) sfx.current.powerup.pause();
        if (sfx.current.gameplay) { sfx.current.gameplay.volume = volumeRef.current.music; sfx.current.gameplay.play().catch(() => {}); }
        // Restore phase timer (continue where we left off)
        gs.phaseStartTime = time;
      }
      // Blink last 2s of frightened
      if (gs.isFrightened && gs.frightenedEnd - time < 2000) {
        const blink = Math.floor(time / 200) % 2 === 0;
        gs.enemies.forEach(e => {
          if (e.isScared) e.setTint(blink ? 0x4444ff : 0xffffff);
        });
      }

      gs.enemies.forEach((enemy, idx) => {
        if (enemy.eaten) {
          // Eaten ghost rushes back to hub
          const hp = ENEMY_HUB_POS[idx];
          const exitX = tx(hp.col), exitY = ty(hp.row);
          const dx = exitX - enemy.x, dy = exitY - enemy.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 4) {
            enemy.col = hp.col; enemy.row = hp.row;
            enemy.x = exitX; enemy.y = exitY;
            enemy.targetX = exitX; enemy.targetY = exitY;
            enemy.setVisible(true);
            enemy.setTexture(enemy.ghostKey);
            enemy.clearTint();
            enemy.setAlpha(1);
            enemy.eaten = false;
            enemy.isInHub = true;
            enemy.released = true;
            enemy.isScared = false;
            enemy.moving = false;
            enemy.path = [];
          } else {
            // Fast speed back to hub
            const rspd = baseEnemySpd * 2;
            enemy.x += (dx / dist) * rspd;
            enemy.y += (dy / dist) * rspd;
          }
          return;
        }

        // Release check
        if (!enemy.released) {
          if (elapsed >= ENEMY_RELEASE[enemy.enemyType]) {
            enemy.released = true;
          } else return;
        }

        // Hub exit
        if (enemy.isInHub) {
          const exitX = tx(HUB_EXIT.col), exitY = ty(HUB_EXIT.row);
          const dx = exitX - enemy.x, dy = exitY - enemy.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 4) {
            enemy.isInHub = false;
            enemy.col = HUB_EXIT.col;
            enemy.row = HUB_EXIT.row;
            enemy.x = exitX; enemy.y = exitY;
            enemy.targetX = exitX; enemy.targetY = exitY;
            enemy.moving = false;
            // Brief grace after hub exit to avoid unfair kills
            enemy.hubGrace = time + 800;
          } else {
            enemy.x += (dx / dist) * 250 * (delta / 1000);
            enemy.y += (dy / dist) * 250 * (delta / 1000);
          }
          return;
        }

        // Reverse direction on mode change
        if (gs.modeJustChanged && !enemy.isScared) {
          enemy.path = [];
          enemy.pathIdx = 0;
          enemy.lastPathCalc = 0; // force immediate recalc
        }

        // Tile-based movement for enemies
        if (enemy.moving) {
          const dx = enemy.targetX - enemy.x;
          const dy = enemy.targetY - enemy.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const s = enemy.isScared ? baseEnemySpd * 0.55 : baseEnemySpd;
          if (dist <= s + 1) {
            enemy.x = enemy.targetX;
            enemy.y = enemy.targetY;
            enemy.moving = false;
            // Immediately try next step (ghosts never stop!)
          } else {
            enemy.x += (dx / dist) * s;
            enemy.y += (dy / dist) * s;
          }
        }

        if (!enemy.moving) {
          // Follow next step in path first
          if (enemy.pathIdx < enemy.path.length) {
            const next = enemy.path[enemy.pathIdx];
            enemy.pathIdx++;
            enemy.col = next.col;
            enemy.row = next.row;
            enemy.targetX = tx(next.col);
            enemy.targetY = ty(next.row);
            enemy.moving = true;
          } else {
            // Path exhausted or empty — recalculate immediately
            // Stage-based AI: L1=dumb, L2=moderate, L3=smart
            const STAGE_AI = [
              { recalcChase: 500, recalcScatter: 700, randomChance: 0.40 },
              { recalcChase: 250, recalcScatter: 450, randomChance: 0.15 },
              { recalcChase: 100, recalcScatter: 250, randomChance: 0.0 },
            ];
            const aiConf = STAGE_AI[gs.stage - 1] || STAGE_AI[2];
            const recalcInterval = (gs.ghostMode === GHOST_MODE.CHASE) ? aiConf.recalcChase : aiConf.recalcScatter;
            if (time - enemy.lastPathCalc >= recalcInterval || enemy.path.length === 0) {
              enemy.lastPathCalc = time;
              let target;
              if (enemy.isScared) {
                // Frightened: move randomly, preferring away from player
                const dirs = [[0, -1], [0, 1], [-1, 0], [1, 0]];
                const w = dirs.filter(d => walkable(grid, enemy.col + d[0], enemy.row + d[1]));
                const away = w.filter(d => {
                  const nc = enemy.col + d[0], nr = enemy.row + d[1];
                  const dToPlayer = Math.abs(nc - gs.pCol) + Math.abs(nr - gs.pRow);
                  const curDist = Math.abs(enemy.col - gs.pCol) + Math.abs(enemy.row - gs.pRow);
                  return dToPlayer >= curDist;
                });
                const pool = away.length > 0 ? away : w;
                if (pool.length > 0) {
                  const rd = pool[Math.floor(Math.random() * pool.length)];
                  target = { col: enemy.col + rd[0], row: enemy.row + rd[1] };
                  enemy.path = [target];
                  enemy.pathIdx = 0;
                }
              } else if (Math.random() < aiConf.randomChance) {
                // Stage-based stupidity: pick random walkable direction
                const dirs = [[0, -1], [0, 1], [-1, 0], [1, 0]];
                const w = dirs.filter(d => walkable(grid, enemy.col + d[0], enemy.row + d[1]));
                if (w.length > 0) {
                  const rd = w[Math.floor(Math.random() * w.length)];
                  enemy.path = [{ col: enemy.col + rd[0], row: enemy.row + rd[1] }];
                  enemy.pathIdx = 0;
                }
              } else {
                // Chase or Scatter — use A*
                let goalTarget;
                if (gs.ghostMode === GHOST_MODE.SCATTER) {
                  goalTarget = getScatterTarget(enemy.enemyType);
                  goalTarget = findNearestWalkable(grid, goalTarget.col, goalTarget.row);
                } else {
                  goalTarget = getChaseTarget(
                    enemy.enemyType, gs.pCol, gs.pRow, currentDir || 'right',
                    gs.enemies[0]?.col || gs.pCol, gs.enemies[0]?.row || gs.pRow,
                    enemy.col, enemy.row
                  );
                  goalTarget = findNearestWalkable(grid, goalTarget.col, goalTarget.row);
                }
                const path = aStar(grid, enemy.col, enemy.row, goalTarget.col, goalTarget.row);
                if (path && path.length > 0) {
                  enemy.path = path;
                  enemy.pathIdx = 0;
                } else {
                  // A* failed — fallback: pick random walkable neighbor (ghosts NEVER stop)
                  const dirs = [[0, -1], [0, 1], [-1, 0], [1, 0]];
                  const w = dirs.filter(d => walkable(grid, enemy.col + d[0], enemy.row + d[1]));
                  if (w.length > 0) {
                    // Prefer direction towards target
                    const sorted = w.sort((a, b) => {
                      const da = Math.abs(enemy.col + a[0] - goalTarget.col) + Math.abs(enemy.row + a[1] - goalTarget.row);
                      const db = Math.abs(enemy.col + b[0] - goalTarget.col) + Math.abs(enemy.row + b[1] - goalTarget.row);
                      return da - db;
                    });
                    const pick = sorted[0];
                    enemy.path = [{ col: enemy.col + pick[0], row: enemy.row + pick[1] }];
                    enemy.pathIdx = 0;
                  }
                }
              }

              // Move immediately if we have a path
              if (enemy.pathIdx < enemy.path.length) {
                const next = enemy.path[enemy.pathIdx];
                enemy.pathIdx++;
                enemy.col = next.col;
                enemy.row = next.row;
                enemy.targetX = tx(next.col);
                enemy.targetY = ty(next.row);
                enemy.moving = true;
              }
            }
          }

          // Tunnel warp (horizontal)
          if (enemy.row === TUNNEL_ROW) {
            if (enemy.col <= 0) { enemy.col = GRID_COLS - 1; enemy.x = tx(enemy.col); enemy.targetX = enemy.x; }
            else if (enemy.col >= GRID_COLS - 1) { enemy.col = 0; enemy.x = tx(enemy.col); enemy.targetX = enemy.x; }
          }
          // Tunnel warp (vertical)
          if (enemy.row <= 0 && gs.grid[0]?.[enemy.col] === CELL.TUNNEL) {
            enemy.row = GRID_ROWS - 1; enemy.y = ty(enemy.row); enemy.targetY = enemy.y;
          } else if (enemy.row >= GRID_ROWS - 1 && gs.grid[GRID_ROWS - 1]?.[enemy.col] === CELL.TUNNEL) {
            enemy.row = 0; enemy.y = ty(enemy.row); enemy.targetY = enemy.y;
          }
        }

        // Collision with player (generous pixel check + nearest-tile check)
        if (!enemy.eaten) {
          const cdx = enemy.x - gs.player.x;
          const cdy = enemy.y - gs.player.y;
          const cDist = Math.sqrt(cdx * cdx + cdy * cdy);
          const eCol = Math.round((enemy.x - HALF) / TILE_SIZE);
          const eRow = Math.round((enemy.y - HALF) / TILE_SIZE);
          const sameTile = (eCol === curCol && eRow === curRow);
          if (cDist < TILE_SIZE * 0.85 || sameTile) {
            if (time < gs.graceEnd) return;
            if (enemy.hubGrace && time < enemy.hubGrace) return;
            if (enemy.isScared) {
              eatEnemy(scene, enemy, idx, time);
            } else {
              loseLife(scene, time);
            }
          }
        }
      });

      // Reset mode change flag
      gs.modeJustChanged = false;

      // ── BONUS PALLET ──
      // Spawn at random walkable position
      if (!gs.bonusPallet && elapsed > 0 && Math.floor(elapsed) % gs.bonusInterval === 0 && Math.floor(elapsed) !== gs.lastBonusSpawn) {
        gs.lastBonusSpawn = Math.floor(elapsed);
        // Find random walkable tile (not hub, not near player)
        const candidates = [];
        for (let r = 2; r < GRID_ROWS - 2; r++) {
          for (let c = 2; c < GRID_COLS - 2; c++) {
            if ((grid[r][c] === CELL.CORRIDOR || grid[r][c] === CELL.TUNNEL) &&
                Math.abs(c - gs.pCol) + Math.abs(r - gs.pRow) > 5) {
              candidates.push({ col: c, row: r });
            }
          }
        }
        if (candidates.length > 0) {
          const pos = candidates[Math.floor(Math.random() * candidates.length)];
          const bx = tx(pos.col), by = ty(pos.row);
          gs.bonusPallet = scene.add.image(bx, by, 'bonus_fruit').setDepth(9);
          gs.bonusPallet.bonusCol = pos.col;
          gs.bonusPallet.bonusRow = pos.row;
          // Pulsing glow animation
          scene.tweens.add({ targets: gs.bonusPallet, alpha: 0.6, duration: 500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
          scene.time.delayedCall(gs.bonusDuration * 1000, () => {
            if (gs.bonusPallet) {
              scene.tweens.add({ targets: gs.bonusPallet, alpha: 0, duration: 400, onComplete: () => {
                if (gs.bonusPallet) { gs.bonusPallet.destroy(); gs.bonusPallet = null; }
              }});
            }
          });
        }
      }
      // Collect bonus (pixel-based position, consistent with pill collection)
      if (gs.bonusPallet && (curCol === gs.bonusPallet.bonusCol && curRow === gs.bonusPallet.bonusRow)) {
        const bx = gs.bonusPallet.x, by = gs.bonusPallet.y;
        gs.bonusPallet.destroy();
        gs.bonusPallet = null;
        gs.score += gs.bonusPoints;
        updateScoreUI();
        // Score popup
        const popup = scene.add.text(bx, by, `+${gs.bonusPoints}`, {
          fontFamily: 'monospace', fontSize: '24px', color: '#ffd700',
          fontStyle: 'bold', stroke: '#000', strokeThickness: 4,
        }).setOrigin(0.5).setDepth(100);
        scene.tweens.add({
          targets: popup, y: popup.y - 50, alpha: 0, duration: 1000,
          onComplete: () => popup.destroy(),
        });
      }
    }

    function collectAt(col, row, scene) {
      const k = `${col},${row}`;
      if (gs.pillSprites[k]) {
        gs.pillSprites[k].destroy();
        delete gs.pillSprites[k];
        gs.score += 10;
        updateScoreUI();
        if (sfx.current.dot) { sfx.current.dot.volume = volumeRef.current.effects; sfx.current.dot.currentTime = 0; sfx.current.dot.play().catch(() => {}); }
      }
      if (gs.powerupSprites[k]) {
        const px = gs.powerupSprites[k].x, py = gs.powerupSprites[k].y;
        gs.powerupSprites[k].destroy();
        delete gs.powerupSprites[k];
        gs.score += 100;
        updateScoreUI();
        activateFrightened(scene);
        // Switch music: pause gameplay, play powerup
        gs.musicPhase = 'powerup';
        if (sfx.current.gameplay) sfx.current.gameplay.pause();
        if (sfx.current.powerup) { sfx.current.powerup.currentTime = 0; sfx.current.powerup.volume = volumeRef.current.music; sfx.current.powerup.play().catch(() => {}); }
        const popup = scene.add.text(px, py, '+100', {
          fontFamily: 'monospace', fontSize: '20px', color: '#4488ff',
          fontStyle: 'bold', stroke: '#000', strokeThickness: 3,
        }).setOrigin(0.5).setDepth(100);
        scene.tweens.add({
          targets: popup, y: popup.y - 40, alpha: 0, duration: 800,
          onComplete: () => popup.destroy(),
        });
      }
      // Check if all pills collected (stage complete)
      if (!gs.stageTransitioning && !gs.gameOver) {
        checkStageComplete(scene);
      }
    }

    function activateFrightened(scene) {
      // Use admin-configured powerup_duration (constant across the game)
      const duration = (settings?.powerup_duration || 8) * 1000;

      gs.isFrightened = true;
      gs.frightenedEnd = scene.time.now + duration;
      gs.ghostEatCombo = 0;
      gs.enemies.forEach(e => {
        if (!e.eaten && !e.isInHub) {
          e.isScared = true;
          e.setTint(0x4444ff);
          // Reverse current path (classic Pac-Man behavior)
          e.path = [];
          e.pathIdx = 0;
          e.lastPathCalc = 0; // force immediate recalc
        }
      });
    }

    function eatEnemy(scene, enemy, idx, time) {
      enemy.setVisible(false);
      enemy.eaten = true;
      enemy.isScared = false;
      if (sfx.current.enemy_eat) { sfx.current.enemy_eat.volume = volumeRef.current.effects; sfx.current.enemy_eat.currentTime = 0; sfx.current.enemy_eat.play().catch(() => {}); }
      // Progressive scoring: 200, 400, 800, 1600
      const points = 200 * Math.pow(2, gs.ghostEatCombo);
      gs.ghostEatCombo = Math.min(gs.ghostEatCombo + 1, 3);
      gs.score += points;
      updateScoreUI();
      // Show score popup at enemy position
      const popup = scene.add.text(enemy.x, enemy.y, `${points}`, {
        fontFamily: 'monospace', fontSize: '20px', color: '#00ffff',
        fontStyle: 'bold', stroke: '#000', strokeThickness: 3,
      }).setOrigin(0.5).setDepth(100);
      scene.tweens.add({
        targets: popup, y: popup.y - 40, alpha: 0, duration: 800,
        onComplete: () => popup.destroy(),
      });
    }

    preloadAll();

    return () => {
      delete window.setGameDirection;
      delete window.__palletmanEndGame;
      window.removeEventListener('resize', refreshScale);
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', refreshScale);
      // Stop all audio but don't clear refs (managed by audio useEffect)
      Object.values(sfx.current).forEach(a => { if (a) a.pause(); });
      if (gameRef.current) { gameRef.current.destroy(true); gameRef.current = null; }
    };
  }, [settings, endGame]);

  const gameBg = settings?.color_game_bg || '#000000';

  return (
    <div data-testid="game-view" className="flex flex-col h-[100dvh] w-screen overflow-hidden" style={{ backgroundColor: gameBg, touchAction: 'none', overscrollBehavior: 'none' }}>
      {loading && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#04082B]">
          <div className="text-[#FFD100] font-mono text-xl mb-4 animate-pulse">CARICAMENTO...</div>
          <div className="w-48 h-2 bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full bg-[#FFD100] rounded-full transition-all duration-500" style={{ width: audioLoaded ? '100%' : '40%' }} />
          </div>
          <div className="text-gray-500 font-mono text-xs mt-3">
            {audioLoaded ? 'Avvio gioco...' : 'Caricamento audio...'}
          </div>
        </div>
      )}
      <div data-testid="game-hud" className="bg-gray-900 text-white font-mono shrink-0" style={{ padding: 'clamp(4px, 1vh, 12px) clamp(8px, 2vw, 16px)' }}>
        {/* Row 1: Score | Stage | Timer */}
        <div className="flex justify-between items-center" style={{ fontSize: 'clamp(1rem, 3vh, 2.5rem)' }}>
          <span ref={scoreDisplayRef} data-testid="score-display" className="font-bold tracking-wider">SCORE 0</span>
          <span ref={stageDisplayRef} data-testid="stage-display" className="font-bold text-green-400 tracking-wider" style={{ fontSize: 'clamp(0.7rem, 2vh, 1.5rem)' }}>STAGE 1</span>
          <span ref={(el) => { timeDisplayRef.current = el; if (el) el.textContent = `${timeRef.current}s`; }} data-testid="time-display" className="font-bold tracking-wider"></span>
        </div>
        {/* Row 2: Lives | Volume */}
        <div className="flex justify-between items-center" style={{ marginTop: 'clamp(2px, 0.5vh, 6px)' }}>
          <div ref={livesDisplayRef} data-testid="lives-display" className="flex items-center gap-1" />
          <button
            onClick={toggleGameAudio}
            className="p-1 rounded hover:bg-gray-700 transition-colors"
            data-testid="game-audio-toggle"
          >
            {audioMuted ? (
              <VolumeX style={{ width: 'clamp(18px, 2.5vh, 32px)', height: 'clamp(18px, 2.5vh, 32px)' }} className="text-gray-500" />
            ) : (
              <Volume2 style={{ width: 'clamp(18px, 2.5vh, 32px)', height: 'clamp(18px, 2.5vh, 32px)' }} className="text-[#FFD100]" />
            )}
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden relative" style={{ backgroundColor: gameBg, touchAction: 'none' }}>
        <div ref={containerRef} data-testid="game-canvas" className="absolute inset-0" style={{ touchAction: 'none' }} />
      </div>
    </div>
  );
};

export default GameView;
